/* eslint-disable operator-linebreak */
import _forOwn from 'lodash/forOwn';
import _indexOf from 'lodash/indexOf';
import uniqid from 'uniqid';
import {
  DRAGGABLE_DIRECTIONS,
  DRAGGABLE_DESTINATION_CHARGERS,
} from './Constants';
// eslint-disable-next-line import/no-cycle
import Enliven from './Enliven';

export function makeActionCreator(type, ...argNames) {
  return (...args) => {
    const action = { type };
    argNames.forEach((arg, index) => {
      action[argNames[index]] = args[index];
    });
    return action;
  };
}

/**
 * @description Search and replace strings for {EXPRESSIONS_LIKE_THIS}
 *
 * @param {string} str - The string to be parsed
 * @param {Object} vars - Object of dynamic substrings
 * @param {Var} vars[key] - The {EXPRESSION} to search for
 * @param {string} vars[key] = value - The value of the {EXPRESSION} after it's evaluated
 *
 * @return {string} parsed string
 */
export function i18n(str, vars = {}) {
  let normalized = str;
  const matches = str.match(/(%% ?[A-Z|_]{1,} ?%%)/g);

  if (matches) {
    // prettier-ignore
    const normalizedMatches = matches.map((match) => {
      const strippedString = match.replace(/(%%)/g, '');
      return strippedString.replace(/(\s)/g, '');
    });

    _forOwn(vars, (value, key) => {
      const i = normalizedMatches.indexOf(key);

      if (i >= 0) {
        normalized = normalized.replace(matches[i], value);
      }
    });
  }

  return normalized;
}

/**
 * @description Gets country code from google maps address components
 *
 * @param {array} components - Collection of address components from Google Maps Places library
 *
 * @return {string} country code (US|CA|GB...)
 */
export function getCountryCode(components) {
  let countryCode;
  // eslint-disable-next-line arrow-parens
  components.map(component => {
    if (component.types[0] === 'country') {
      countryCode = component.short_name;
    }
    return component;
  });

  return countryCode;
}

/**
 * @description Formats Google Maps address components into a shorter address for handheld devices
 *
 * @param {Object} place - Location details
 *
 * @return {string} formatted short address for devices with less real estate
 */
export function getShortAddressFromAddressComponents(place) {
  let i = 0;
  const addressLen = place.address_components.length;
  let shortAddress = [];
  for (i; i < addressLen; i += 1) {
    if (i <= 2) {
      shortAddress.push(place.address_components[i].short_name);
    } else {
      break;
    }
  }
  shortAddress = shortAddress.join(' ');

  return shortAddress;
}

/**
 * @description Parses URI parameters
 *
 * @return {Object} key: value pairs
 */
export function getJsonFromUrl() {
  // eslint-disable-next-line no-restricted-globals
  const query = location.hash.replace('#/', '').substr(1);
  const result = {};
  // eslint-disable-next-line arrow-parens
  query.split('&').forEach(part => {
    const item = part.split('=');
    result[item[0]] = decodeURIComponent(item[1]);
  });
  return result;
}

/**
 * @description Get height of desired node
 *
 * @param {string} id - uniquie identifier for DOM node
 *
 * @return {integer} key: value pairs
 */
export function getNodeHeight(id) {
  let h = 0;
  try {
    h = document.getElementById(id).offsetHeight;
  } catch (e) {
    try {
      h = document.getElementsByClassName(id)[0].offsetHeight;
    } catch (err) {
      // Cannot find node
    }
  }

  return h;
}

/**
 * @description Maps invalid country codes to their nearest geographical neighbor
 *
 * @param {string} region - Country code
 *
 * @return {string} valid country code
 */
export function countryWorkarounds(region) {
  if (region === 'EU') return 'DE';
  if (region === 'MO') return 'HK';

  return region;
}

/**
 * @description Maps invalid language codes to correct language per
 *              https://developers.google.com/maps/faq#languagesupport
 *
 * @param {string} language - Language code
 * @param {string} region - Country code
 *
 * @return {string} valid language code
 */
export function normalizeMapLanguage(language, region) {
  if (language === 'zh' && region === 'TW') return 'zh-TW';

  return language;
}

/**
 * @description Maps invalid language to correct language for incentives API
 *
 * @param {string} language - Language code
 * @param {string} region - Country code
 *
 * @return {string} valid language code
 */
export function normalizeCalcLanguage(language, region) {
  const lang = language.toLowerCase();
  if (lang === 'en' && region === 'MO') return 'zh';
  if (lang === 'de' && region === 'LU') return 'fr';

  return lang;
}

/**
 * @description Returns jp when ja_JP and '' when en_US
 *
 * @param {string} locale - locale string "en_US"
 *
 * @return {string} valid locale
 */
export function fixLocalePath(locale) {
  let localeStr = locale;
  if (locale === 'ja_JP') localeStr = 'jp';
  if (locale === 'en_US') localeStr = '';

  if (localeStr !== '') {
    localeStr = `/${localeStr}`;
  }

  return localeStr;
}

/**
 * @description Temporarily prepend locale to hyperlinks in tooltip body
 *
 * @param {string} str - copy to parse
 * @param {string} locale - locale string "en_US"
 *
 * @return {string} string with injected locale
 */
export function prependLocaleToHyperlink({ str, locale }) {
  const localeStr = fixLocalePath(locale);

  return str.replace('href="/', `href="${localeStr}/`);
}

/**
 * @description Massage fuel incentives data
 *
 * @param {Object} data - Fuel savings data
 *
 * @return {Object} normalized data
 */
export function normalizeIncentivesData(data) {
  const newData = data.map(
    currentValue =>
      // eslint-disable-next-line implicit-arrow-linebreak
      Object.assign({}, currentValue, {
        variables: Object.assign({}, currentValue.variables, {
          distance_per_month: currentValue.variables.dist_per_mo,
          fuel_efficiency_imperial: currentValue.variables.comparable_mpg,
          fuel_efficiency_metric:
            currentValue.variables.comparable_mpg ||
            currentValue.variables.benchmark_fuel_economy,
          fuel_price: currentValue.variables.fuel_price,
          kwh_consumption: currentValue.variables.kwh,
          supercharger_kwh_price: '', // TODO:
        }),
      }),
    // eslint-disable-next-line function-paren-newline
  );

  return newData[0];
}

/*
 * * @description Workaround for GB fuel_efficiency_metric
 *
 * @param {Object} data - Fuel savings data
 *
 * @return {Object} corrected value
 */
export function fixFuelEfficiencyData(country, incentivesData) {
  let newData = incentivesData;

  if (country === 'GB') {
    newData = Object.assign({}, incentivesData, {
      variables: Object.assign({}, incentivesData.variables, {
        fuel_efficiency_metric: 14.6825,
      }),
    });
  }

  return newData;
}

/**
 * @description Creates a string of data from a input field Object
 *
 * @param {Obect} field - Orgin|Stopover|Destination details
 *
 * @return {string}
 */
export function constructURIFromField(field) {
  const address = `${encodeURI(field.address)}_`;
  const shortAddress = `${encodeURI(field.shortAddress)}@`;
  const latLng = `${encodeURI(field.lat)},${encodeURI(field.lng)}`;
  const place = address + shortAddress + latLng;

  return place;
}

/**
 * @description Creates a string of all stopovers from the users trip
 *
 * @param {Array} fields - Trip details
 *
 * @return {string} + delimited string of stopovers
 */
export function constructStopsFromStopovers(fields) {
  let stops = '';

  const fieldsLen = fields.length;
  if (fieldsLen > 2) {
    const trip = fields.slice();
    const stopovers = trip.slice(1, -1);

    let i = 0;
    const stopsLen = stopovers.length;
    for (i; i < stopsLen; i += 1) {
      stops += `${constructURIFromField(stopovers[i])}${
        stopsLen > 1 && i < stopsLen - 1 ? '+' : ''
      }`;
    }
  }

  return stops;
}

/**
 * @description Append URI encoded string of trip data
 *
 * @param {Object} vehicle - Selected vehicle data
 * @param {Object} places - origin, stopovers, & destination data
 *
 * @return {void}
 */
export function saveTrip({ vehicle, places }) {
  // encode trip params
  const origin = constructURIFromField(places.fields[0]);
  const stops = constructStopsFromStopovers(places.fields);
  const destination = constructURIFromField(
    places.fields[places.fields.length - 1],
  );

  const newURL = `?v=${vehicle.model}&o=${origin}&s=${stops}&d=${destination}`;

  // append to URL
  window.location.hash = '/';
  window.location.href = `${window.location.href}${newURL}`;
}

/**
 * @description Append URI encoded string of trip data
 *
 * @param {Object} vehicle - Selected vehicle data
 * @param {Object} places - origin, stopovers, & destination data
 *
 * @return {void}
 */
export function decodePlace(field, order) {
  const decoded = decodeURI(field);

  const addresses = decoded.split('_');
  const address = addresses[0];
  const shortAddress = addresses[1].replace(
    /@(-?\d{1,}.\d{1,}),(-?\d{1,}.\d{1,})/g,
    '',
  );

  const geopointStart = addresses[1].indexOf('@');
  const latLng = addresses[1]
    .substr(geopointStart, addresses[1].length)
    .match(/(-?\d{1,}.\d{1,})/g);
  const lat = Number(latLng[0]);
  const lng = Number(latLng[1]);

  const place = {
    id: uniqid(),
    address,
    shortAddress,
    lat,
    lng,
    order,
  };

  return place;
}

/**
 * @description Constructs objet of trip data for dispatching to Redux store so that a user can
 *              resume their trip.
 *
 * @param {Object} vehicle - Selected vehicle data
 * @param {Object} origin - Trip origin details
 * @param {Object} stops - Trip stopover details
 * @param {Object} destination - Trip destination details
 *
 * @return {Object} trip data to dispatch to Redux store
 */
// eslint-disable-next-line object-curly-newline
export function constructTripData({ vehicle, origin, stops, destination }) {
  const tripData = {
    vehicle: {
      model: vehicle,
    },
    places: {
      fields: [],
    },
  };

  let order = 0;

  // push in origin
  tripData.places.fields.push(decodePlace(origin, order));

  // push in stopovers
  // eslint-disable-next-line arrow-parens
  stops.split('+').forEach(place => {
    if (place) {
      order += 1;
      tripData.places.fields.push(decodePlace(place, order));
    }
  });

  // push in destination
  tripData.places.fields.push(decodePlace(destination, order + 1));

  return tripData;
}

/**
 * @description Parses URL for trip data so a user may resume their trip
 *
 * @return {Object} vehicle, origin, stopover, & destination details
 */
export function getTripFromURL() {
  const url = window.location.href;
  const queryParm = url.indexOf('?v=');

  let vehicle = '';
  let origin = '';
  let stops = '';
  let destination = '';

  const trip = url.substr(queryParm + 3);
  trip.split('&').forEach((part, index) => {
    // vehicle
    if (index === 0) vehicle = part;

    // origin
    if (part.indexOf('o=') >= 0) origin = part.replace('o=', '');

    // stops
    if (part.indexOf('s=') >= 0) stops = part.replace('s=', '');

    // destination
    if (part.indexOf('d=') >= 0) destination = part.replace('d=', '');
  });

  // eslint-disable-next-line object-curly-newline
  return { vehicle, origin, stops, destination };
}

/**
 * @description Clears everything after the hash
 *
 * @return {Void}
 */
export function clearFromURLHash() {
  window.location.hash = '';
}

/**
 * @description Returns the height from array of components
 *
 * @param {Array} components - Array of components with a dynamic height
 * @param {String} component - The name of the component
 *
 * @return {Object} height - height of desired component
 */
export function getComponentHeight({ components, component }) {
  return components.filter(item => item.component === component)[0].height;
}

/**
 * @description Animates the Directions component to the left or right
 *
 * @return {void}
 */
export function animateDirectionsComponent(closeInfoWindow = false) {
  const directionsSelector = DRAGGABLE_DIRECTIONS;
  const directionsXPos = Enliven.getTranslateX(directionsSelector);
  const directionsYPos = Enliven.getTranslateY(directionsSelector);

  const destSelector = DRAGGABLE_DESTINATION_CHARGERS;
  const destXPos = Enliven.getTranslateX(destSelector);

  if (directionsXPos === 0) {
    Enliven.animate(
      directionsSelector,
      'slideLeft',
      0,
      '-100%',
      directionsYPos,
      directionsYPos,
    );
  } else if (closeInfoWindow && destXPos !== -100) {
    Enliven.animate(
      directionsSelector,
      'slideRight',
      '-100%',
      0,
      directionsYPos,
      directionsYPos,
    );
  }
}

/**
 * @description Animates the DestinationCharger component to the left or right
 *
 * @return {void}
 */
export function animateDestinationChargerComponent(closeInfoWindow = false) {
  const destSelector = DRAGGABLE_DESTINATION_CHARGERS;
  const destXPos = Enliven.getTranslateX(destSelector);
  const destYPos = Enliven.getTranslateY(destSelector);

  if (destXPos === -100) {
    Enliven.animate(
      destSelector,
      'slide-left',
      destXPos,
      '-200%',
      destYPos,
      destYPos,
    );
  } else if (destXPos === -200 && closeInfoWindow) {
    Enliven.animate(
      destSelector,
      'slide-right',
      '-200%',
      '-100%',
      destYPos,
      destYPos,
    );
  }
}
